import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportTicket, ISupportTicketSummary } from '../../../models/Support/ISupportTicket';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Button } from '../../../web/components-v2/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { ticketSummaryPopoverStyles as sp } from './styles/ticketSummaryPopoverStyles';
import { formatShortDateTime } from './supportTicketPresentation';

interface TicketSummaryPopoverProps {
	ticket: ISupportTicket;
}

const SECTIONS: { label: string; key: keyof Omit<ISupportTicketSummary, 'generatedAt'> }[] = [
	{ label: 'Sentiment', key: 'sentiment' },
	{ label: 'Issue', key: 'issue' },
	{ label: 'Resolution', key: 'resolution' },
	{ label: 'CSM watch-outs', key: 'csmWatchOuts' },
];

/**
 * Shows the AI's summary of a resolved ticket.
 *
 * Gated on the summary itself rather than the status: the API unsets it when a ticket is reopened or
 * moved to another account, so a stored summary is by definition a current one — and that also keeps
 * the panel on an archived ticket, which stays resolved and keeps its summary.
 */
export const TicketSummaryPopover: React.FC<TicketSummaryPopoverProps> = ({ ticket }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const summary = ticket.summary;
	if (!summary) return null;

	const sections = SECTIONS.filter(section => summary[section.key]?.trim());

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button type='button' variant='ghost' styles={[sp.trigger]} title='AI summary' aria-label='AI summary'>
					<SparklesIcon width={13} height={13} />
					Summary
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' sideOffset={8} collisionPadding={12} styles={[sp.popover]}>
				<div className={css(sp.header)}>
					<span className={css(sp.title)}>
						<SparklesIcon width={16} height={16} className={css(sp.titleIcon)} />
						AI Summary
					</span>
					<span className={css(sp.generatedAt)}>{formatShortDateTime(summary.generatedAt)}</span>
				</div>
				<div className={css(sp.body)}>
					{sections.length === 0 ? (
						<div className={css(sp.state)}>This summary came back empty.</div>
					) : (
						sections.map(section => (
							<div key={section.key} className={css(sp.section)}>
								<div className={css(sp.sectionLabel)}>{section.label}</div>
								<div className={css(sp.sectionValue)}>{summary[section.key]}</div>
							</div>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
